//CoolEarthCalculator.jsx (TaskList Component)
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCity, faGlobe, faTree, faPlus } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import MainLayout from '../../layouts/MainLayout';


const CoolEarthCalculator = () => {
  const location = useLocation();
  const authState = useSelector(state => state.authReducer);
  const [tasks, setTasks] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [name, setName] = useState('');
  
  const [activeTab, setActiveTab] = useState("Welcome");
  const [howToClicked, setHowToClicked] = useState(false);

  const tabs = [
    "How To",
    "Welcome",
    "House",
    "Flights",
    "Car",
    "Motorbike",
    "Bus & Rail",
    "Plastic",
    "Electrical",
    "Secondary",
    "Results",
  ];

  const [householdSize, setHouseholdSize] = useState(1);
  const [electricity, setElectricity] = useState(0);
  const [naturalGas, setNaturalGas] = useState(0);
  const [heatingOil, setHeatingOil] = useState(0);
  const [coal, setCoal] = useState(0);
  const [lpg, setLpg] = useState(0);
  const [propane, setPropane] = useState(0);
  const [woodPellets, setWoodPellets] = useState(0);
  const [totalFootprint, setTotalFootprint] = useState(0);

  const calculateFootprint = () => {
    const electricityCO2 = electricity * 0.3937;
    const totalCO2 =
      electricityCO2 +
      naturalGas +
      heatingOil +
      coal +
      lpg +
      propane +
      woodPellets;
  
    setTotalFootprint((totalCO2 / householdSize).toFixed(2));
  };

const [tripType, setTripType] = useState("return");
const [from, setFrom] = useState("");
const [to, setTo] = useState("");
const [via, setVia] = useState("");
const [flightClass, setFlightClass] = useState("economy");
const [trips, setTrips] = useState(1);
const [includeRF, setIncludeRF] = useState(false);
const [totalFlightFootprint, setTotalFlightFootprint] = useState(0);

// Example emission factors (kg CO2e per passenger km)
const emissionFactorsFlights = {
  economy: 0.15,
  business: 0.27,
  first: 0.40,
};

const calculateFlightFootprint = () => {
  if (!from || !to) {
    alert("Please select both departure and destination!");
    return;
  }

  // Simulating distance calculation (Replace with real API)
  const distance = 1500; // Placeholder for distance in km

  let footprint = distance * emissionFactorsFlights[flightClass] * trips;

  if (tripType === "return") {
    footprint *= 2;
  }

  if (includeRF) {
    footprint *= 1.9; // Applying radiative forcing factor
  }

  setTotalFlightFootprint(footprint.toFixed(2));
};

const [mileage, setMileage] = useState(0);
const [year, setYear] = useState("");
const [mpg, setMpg] = useState("");
const [fuelType, setFuelType] = useState("petrol");
const [totalCarFootprint, setTotalCarFootprint] = useState(0);

// Example CO2 emission factors (kg CO2e per gallon)
const fuelEmissionFactors = {
  petrol: 8.89, // kg CO2 per gallon
  diesel: 10.16,
  electric: 0, // Assuming renewable energy
};

const calculateCarFootprint = () => {
  if (!mileage || (!mpg && !year)) {
    alert("Please enter mileage and either MPG or select a car year!");
    return;
  }

  let fuelUsed = mileage / (mpg || 25); // Default to 25 MPG if not entered
  let footprint = fuelUsed * fuelEmissionFactors[fuelType];

  setTotalCarFootprint(footprint.toFixed(2));
};

const [bikeMileage, setBikeMileage] = useState(0);
const [bikeType, setBikeType] = useState("");
const [bikeMpg, setBikeMpg] = useState("");
const [bikeFootprint, setBikeFootprint] = useState(0);

// Example CO2 emission factors (kg CO2e per gallon)
const bikeEmissionFactor = 8.07; // Average for petrol bikes (kg CO2 per gallon)

const calculateBikeFootprint = () => {
  if (!bikeMileage || (!bikeMpg && !bikeType)) {
    alert("Please enter mileage and either MPG or select a motorbike type!");
    return;
  }

  let fuelUsed = bikeMileage / (bikeMpg || 50); // Default to 50 MPG if not entered
  let footprint = fuelUsed * bikeEmissionFactor;

  setBikeFootprint(footprint.toFixed(2));
};

const [busMiles, setBusMiles] = useState(0);
const [coachMiles, setCoachMiles] = useState(0);
const [localTrainMiles, setLocalTrainMiles] = useState(0);
const [longTrainMiles, setLongTrainMiles] = useState(0);
const [tramMiles, setTramMiles] = useState(0);
const [subwayMiles, setSubwayMiles] = useState(0);
const [taxiMiles, setTaxiMiles] = useState(0);
const [publicFootprint, setPublicFootprint] = useState(0);

// Example CO2 emission factors (kg CO2e per mile)
const emissionFactorsPublic = {
  bus: 0.105,
  coach: 0.05,
  localTrain: 0.06,
  longTrain: 0.04,
  tram: 0.045,
  subway: 0.041,
  taxi: 0.2,
};

const calculatePublicFootprint = () => {
  let totalEmissions =
    busMiles * emissionFactorsPublic.bus +
    coachMiles * emissionFactorsPublic.coach +
    localTrainMiles * emissionFactorsPublic.localTrain +
    longTrainMiles * emissionFactorsPublic.longTrain +
    tramMiles * emissionFactorsPublic.tram +
    subwayMiles * emissionFactorsPublic.subway +
    taxiMiles * emissionFactorsPublic.taxi;

  setPublicFootprint(totalEmissions.toFixed(2));
};

const [currency, setCurrency] = useState("₹ INR");
const [expenses, setExpenses] = useState({
  food: 0,
  pharmaceuticals: 0,
  clothes: 0,
  paperProducts: 0,
  computers: 0,
  electronics: 0,
  vehicles: 0,
  furniture: 0,
  hospitality: 0,
  telecom: 0,
  finance: 0,
  insurance: 0,
  education: 0,
  recreation: 0,
});
const [secondaryFootprint, setSecondaryFootprint] = useState(0);

// Example emission factors (kg CO2e per Rs. 1000 spent)
const emissionFactorsSecondary = {
  food: 0.5,
  pharmaceuticals: 0.3,
  clothes: 0.6,
  paperProducts: 0.4,
  computers: 0.7,
  electronics: 0.8,
  vehicles: 1.2,
  furniture: 0.9,
  hospitality: 0.5,
  telecom: 0.4,
  finance: 0.3,
  insurance: 0.3,
  education: 0.2,
  recreation: 0.6,
};

const calculateSecondaryFootprint = () => {
  let totalEmissions = Object.keys(expenses).reduce(
    (total, key) => total + (expenses[key] / 1000) * emissionFactorsSecondary[key],
    0
  );

  setSecondaryFootprint(totalEmissions.toFixed(2));
};

const [selectedOffsets, setSelectedOffsets] = useState([]);
const [totalOffset, setTotalOffset] = useState(0);

const ResultTab = ({ footprintData }) => {
  const totalFootprint = Object.values(footprintData).reduce((a, b) => a + b, 0);
  totalOffset = selectedOffsets.reduce((a, b) => a + footprintData[b], 0);
  };

  // const toggleOffset = (category) => {
  //   setSelectedOffsets((prev) =>
  //     prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
  //   );

  const [homePlastics, setHomePlastics] = useState({
    foodPackaging: 0,
    toiletries: 0,
    cleaningProducts: 0,
    cottonBuds: 0,
    toothbrushes: 0,
  });

  const [outPlastics, setOutPlastics] = useState({
    takeawayCups: 0,
    bottledDrinks: 0,
    plasticBags: 0,
    strawsStirrers: 0,
  });

  const calculateTotal = (plastics) =>
    Object.values(plastics).reduce((acc, val) => acc + parseInt(val || 0), 0);

  const conversionRate = 105; // GBP to INR conversion

  const energyData = [
    {
      category: "Kitchen",
      appliance: "Microwave Oven",
      usage: "96 times/year",
      cost: 9.07,
      co2: 39,
    },
    {
      category: "Kitchen",
      appliance: "Washing Machine",
      usage: "187 washes/year",
      cost: 11.78,
      co2: 51,
    },
    {
      category: "Kitchen",
      appliance: "Electric Tumble Dryer",
      usage: "148 uses/year",
      cost: 37.0,
      co2: 159,
    },
    {
      category: "Lighting",
      appliance: "Standard Light Bulb",
      usage: "4 hours/day",
      cost: 14.6,
      co2: 63,
    },
    {
      category: "Lighting",
      appliance: "Low Energy Light Bulb",
      usage: "4 hours/day",
      cost: 2.63,
      co2: 11,
    },
    {
      category: "Entertainment",
      appliance: "Primary TV - Plasma 34-37 inch",
      usage: "6.5 hours/day",
      cost: 62.61,
      co2: 269,
    },
    {
      category: "Entertainment",
      appliance: "Primary TV - LCD 34-37 inch",
      usage: "6.5 hours/day",
      cost: 50.08,
      co2: 215,
    },
  ];

  const [sortBy, setSortBy] = useState("cost");

  const sortedData = [...energyData].sort((a, b) => a[sortBy] - b[sortBy]);


  return (
    <>
      <MainLayout>
        <div className="mt-12 bg-gray-600 min-h-[100vh] py-4 px-4">
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-1 mb-4 flex-wrap sm:text-xs">
              <div className="flex w-full">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    className={`flex-1 px-4 py-3 text-center whitespace-nowrap 
                      border-b-2 ${
                        activeTab === tab
                          ? "bg-blue-500 text-white border-blue-700"
                          : "bg-gray-300 text-gray-800 border-transparent"
                      }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            {/* <div> */}
              {/* {activeTab === "Welcome" && <p className='px-4 text-yellow-200'>Welcome to the web's leading carbon footprint calculator.</p>} */}
              {/* {activeTab === "House" && <p className='px-4 text-yellow-200'>Calculate emissions from home energy usage.</p>} */}
              {/* {activeTab === "Flights" && <p className='px-4 text-yellow-200'>Calculate your emissions from air travel.</p>} */}
              {/* {activeTab === "Car" && <p className='px-4 text-yellow-200'>Calculate your emissions from car travel.</p>}
              {activeTab === "Motorbike" && <p className='px-4 text-yellow-200'>Calculate emissions from motorbike usage.</p>}
              {activeTab === "Bus & Rail" && <p className='px-4 text-yellow-200'>Estimate emissions from public transport.</p>}
              {activeTab === "Secondary" && <p className='px-4 text-yellow-200'>Track emissions from indirect sources.</p>}
              {activeTab === "Results" && <p className='px-4 text-yellow-200'>View your total carbon footprint results.</p>}             */}
            {/* </div> */}
            <div className="flex flex-row p-4 text-blue-600 shadow-md rounded-lg">
              {activeTab === "How To" && (
                <div className="flex flex-col mt-4 text-center">
                  <div class="bg-gray-700">
                    <h1 class="text-3xl font-bold text-green-600 mb-4">How to reduce your footprint?: Carbon Reduction Tips</h1>
                    <p class="text-lg text-white">Helping you reduce your footprint with practical advice on cutting carbon and energy costs.</p>

                    <div class="bg-green-600 p-4 rounded-lg mt-4">
                      <p class="text-lg font-semibold">The average family of four creates 10 tonnes of CO2 emissions each year.</p>
                      <p class="text-lg text-white">Use our <a href="#" class="text-green-700 underline">free calculator</a> to find out your CO2 emissions.</p>
                    </div>

                    <h2 class="text-2xl font-semibold text-green-600 mt-6">Top 5 Cost-Saving Actions</h2>
                    <ul class="list-decimal list-inside mt-2 text-white">
                      <li><span class="font-semibold">Hang out the washing</span> instead of using a tumble dryer – saves 153kg CO2 and £52 ($68) per year.</li>
                      <li><span class="font-semibold">Turn down the heating by 1°C</span> – reduces energy use by 8%, saving 184kg CO2 and £42 ($55) annually.</li>
                      <li><span class="font-semibold">Only boil the amount of water you need</span> – saves 72kg CO2 and £23 ($30) per year.</li>
                      <li><span class="font-semibold">Spend 1 minute less in the shower</span> – saves 23kg CO2 and £8 ($10) annually.</li>
                      <li><span class="font-semibold">Turn off electrical equipment</span> when not in use – saves about 5kg CO2 and £2 ($2.64) per year per device.</li>
                    </ul>
                    <p class="mt-2 font-semibold text-white">Total Potential Savings: 437kg CO2 and £127 annually!</p>
                    
                    {/* <div class="bg-blue-600 p-4 rounded-lg mt-4">
                      <p class="text-lg font-semibold">Download our <a href="#" class="text-blue-700 underline">energy savings chart</a> to see which electrical items contribute most to your carbon footprint.</p>
                    </div> */}

                    <h2 class="text-2xl font-semibold text-green-600 mt-6">Technology-Based Energy Savings</h2>
                    <ul class="list-disc list-inside text-white">
                      <li>Switch to <span class="font-semibold">LED light bulbs</span> to cut 90% of lighting costs.</li>
                      <li>Install <span class="font-semibold">thermostatic radiator valves</span> to regulate heating efficiently.</li>
                      <li><span class="font-semibold">Insulate your hot water tank</span> to reduce heat loss.</li>
                      <li>Consider <span class="font-semibold">cavity wall and loft insulation</span> for better energy retention.</li>
                      <li>Upgrade to <span class="font-semibold">an A++ rated refrigerator/freezer</span> if yours is over 15 years old.</li>
                      <li>Replace an old boiler with <span class="font-semibold">a high-efficiency condensing boiler</span>.</li>
                    </ul>

                    <h2 class="text-2xl font-semibold text-green-600 mt-6">Travel Smarter</h2>
                    <ul class="list-disc list-inside text-white">
                      <li>Carpool for work and school runs.</li>
                      <li>Use public transport where possible.</li>
                      <li>Walk or cycle for short trips.</li>
                      <li>Reduce air travel and opt for video meetings where possible.</li>
                      <li>Choose a low-emission or electric vehicle when replacing your car.</li>
                      <li>Turn off hotel room lights and air-conditioning when not in use.</li>
                    </ul>

                    <h2 class="text-2xl font-semibold text-green-600 mt-6">Reduce Your Secondary Carbon Footprint</h2>
                    <ul class="list-disc list-inside text-white">
                      <li>Avoid bottled water if tap water is safe.</li>
                      <li>Buy local and seasonal produce.</li>
                      <li>Reduce meat consumption.</li>
                      <li>Choose locally-made products over imports.</li>
                      <li>Recycle and minimize packaging waste.</li>
                    </ul>

                    <h2 class="text-2xl font-semibold text-green-600 mt-6">Offset Your Emissions</h2>
                    <p class="text-white">Reducing emissions to zero is tough. Consider <a href="#" class="text-green-700 underline">offsetting your carbon footprint</a> for any remaining emissions.</p>
                    
                    <h2 class="text-2xl font-semibold text-green-600 mt-6">Reduce Your Plastic Waste</h2>
                    <p class="text-white">Follow the 4 R’s – <span class="font-semibold">Refuse, Reduce, Reuse, Recycle</span>. Check our <a href="#" class="text-green-700 underline">Plastic Waste Guide</a> for tips.</p>
                  </div>
                </div>
              )}
              {activeTab === "Welcome" && (
                <div className='text-white bg-gray-700 px-4 py-4'>
                  <p className="mb-4 text-yellow-200">First, please tell us where you live:</p>

                  {/* Country Selection */}
                  <div className="mb-4 text-black">
                    <label className="block font-semibold mb-1">
                      Country:
                    </label>
                    <select style={{width: "200px"}} className="w-full p-2 border rounded-lg h-10">
                      <option>India</option>
                      <option>United States</option>
                      <option>United Kingdom</option>
                      <option>Canada</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label className="block font-semibold mb-1">
                      State:
                    </label>
                    <select style={{width: "200px"}} className="w-full p-2 border rounded-lg text-black h-10">
                      <option>Average for country</option>
                      <option>California</option>
                      <option>Texas</option>
                      <option>New York</option>
                    </select>
                  </div>

                  <p className="mb-4">
                    Carbon footprint calculations are typically based on annual emissions from the previous 12 months.
                  </p>

                  <div className="mb-4">
                    <label className="block font-semibold mb-1">
                      Enter the period this calculation covers (optional):
                    </label>
                    <div className="flex gap-4 text-black">
                      <input type="date" style={{width: "200px"}} className="w-1/2 p-2 border rounded-lg" />
                      <input type="date" style={{width: "200px"}} className="w-1/2 p-2 border rounded-lg" />
                    </div>
                  </div>

                  <p className=" mb-4">
                    Next, select the appropriate tab above to calculate the part of your lifestyle you are most interested in, e.g., your flights.
                  </p>

                  <p className="">
                    Or, visit each of the tabs above to calculate your full carbon footprint.
                  </p>

                  <p className="mt-4 font-semibold">
                    Following your calculation, you can offset/neutralize your emissions through one of our climate-friendly projects.
                  </p>
                </div>
              )}
            {activeTab === "House" && 
              <div className="p-4 bg-gray-700 rounded-lg">
                <h2 className="text-lg font-bold">Calculate the Carbon Footprint for Your Home</h2>
                <p className="text-sm text-white">
                  Enter your consumption of each type of energy, and press the Calculate button.
                </p>
            
                <div className="mt-4">
                  <label className="block">How many people are in your household?</label>
                  <input
                    type="number"
                    value={householdSize}
                    min="1"
                    className="w-full p-2 border rounded-md"
                    onChange={(e) => setHouseholdSize(e.target.value)}
                  />
                </div>
            
                {[
                  { label: "Electricity (kWh)", value: electricity, setter: setElectricity },
                  { label: "Natural Gas (kWh)", value: naturalGas, setter: setNaturalGas },
                  { label: "Heating Oil (US gallons)", value: heatingOil, setter: setHeatingOil },
                  { label: "Coal (kWh)", value: coal, setter: setCoal },
                  { label: "LPG (therms)", value: lpg, setter: setLpg },
                  { label: "Propane (US gallons)", value: propane, setter: setPropane },
                  { label: "Wooden Pellets (metric tons)", value: woodPellets, setter: setWoodPellets },
                ].map((item, index) => (
                  <div key={index} className="mt-3">
                    <label className="block">{item.label}</label>
                    <input
                      type="number"
                      value={item.value}
                      className="w-full p-2 border rounded-md"
                      onChange={(e) => item.setter(e.target.value)}
                    />
                  </div>
                ))}
            
                <button
                  className="w-full mt-4 p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  onClick={calculateFootprint}
                >
                  Calculate
                </button>
            
                <p className="mt-4 text-lg font-bold">
                  Total House Footprint = {totalFootprint} metric tons of CO2e
                </p>
            
                <button className="w-full mt-2 p-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                  Offset Now
                </button>
              </div>
            }

            {activeTab === "Flights" && 
              <div className="p-4 bg-gray-700 rounded-lg">
                <h2 className="text-lg font-bold">Calculate the Carbon Footprint for Your Flights</h2>
                <p className="text-sm text-white">
                  Enter your flight details below. You can add up to 3 itineraries.
                </p>
            
                <div className="mt-4 flex space-x-4">
                  {["return", "one-way"].map((type) => (
                    <button
                      key={type}
                      className={`flex-1 px-4 py-2 rounded-md ${
                        tripType === type ? "bg-blue-500 text-white" : "bg-gray-300 text-gray-700"
                      }`}
                      onClick={() => setTripType(type)}
                    >
                      {type === "return" ? "Return Trip" : "One-Way Flight"}
                    </button>
                  ))}
                </div>
            
                <div className="mt-4">
                  <label className="block">From:</label>
                  <select className="w-full p-2 border rounded-md" value={from} onChange={(e) => setFrom(e.target.value)}>
                    <option value="">Select</option>
                    <option value="NYC">New York</option>
                    <option value="LON">London</option>
                    <option value="DEL">Delhi</option>
                  </select>
                </div>
            
                <div className="mt-2">
                  <label className="block">To:</label>
                  <select className="w-full p-2 border rounded-md" value={to} onChange={(e) => setTo(e.target.value)}>
                    <option value="">Select</option>
                    <option value="NYC">New York</option>
                    <option value="LON">London</option>
                    <option value="DEL">Delhi</option>
                  </select>
                </div>
            
                <div className="mt-2">
                  <label className="block">Via (optional):</label>
                  <select className="w-full p-2 border rounded-md" value={via} onChange={(e) => setVia(e.target.value)}>
                    <option value="">Select</option>
                    <option value="DXB">Dubai</option>
                    <option value="FRA">Frankfurt</option>
                  </select>
                </div>
            
                <div className="mt-2">
                  <label className="block">Class:</label>
                  <select className="w-full p-2 border rounded-md" value={flightClass} onChange={(e) => setFlightClass(e.target.value)}>
                    <option value="economy">Economy</option>
                    <option value="business">Business</option>
                    <option value="first">First</option>
                  </select>
                </div>
            
                <div className="mt-2">
                  <label className="block">Trips:</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full p-2 border rounded-md"
                    value={trips}
                    onChange={(e) => setTrips(e.target.value)}
                  />
                </div>
            
                <div className="mt-3 flex items-center">
                  <input type="checkbox" id="rf" checked={includeRF} onChange={() => setIncludeRF(!includeRF)} />
                  <label htmlFor="rf" className="ml-2">Include Radiative Forcing</label>
                </div>
            
                <button
                  className="w-full mt-4 p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  onClick={calculateFlightFootprint}
                >
                  Calculate
                </button>
            
                <p className="mt-4 text-lg font-bold">
                  Total Flights Footprint = {totalFlightFootprint} metric tons of CO2e
                </p>
            
                <button className="w-full mt-2 p-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                  Offset Now
                </button>
            
                <div className="flex justify-between mt-4">
                  <button className="px-4 py-2 bg-gray-300 rounded-lg">&lt; House</button>
                  <button className="px-4 py-2 bg-gray-300 rounded-lg">Car &gt;</button>
                </div>
              </div>
            }
            {activeTab === "Car" && 
              <div className="p-4 bg-gray-700 rounded-lg">
                <h2 className="text-lg font-bold">Calculate the Carbon Footprint for Your Cars</h2>
                <p className="text-sm text-white">You can enter details for up to 2 cars.</p>
            
                {/* Mileage Input */}
                <div className="mt-4">
                  <label className="block">Mileage (miles):</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-md"
                    value={mileage}
                    onChange={(e) => setMileage(e.target.value)}
                  />
                </div>
            
                {/* Vehicle Year Selector */}
                <div className="mt-2">
                  <label className="block">Choose vehicle:</label>
                  <select className="w-full p-2 border rounded-md" value={year} onChange={(e) => setYear(e.target.value)}>
                    <option value="">- Select Year of Manufacture -</option>
                    <option value="2024">2024</option>
                    <option value="2023">2023</option>
                    <option value="2022">2022</option>
                  </select>
                </div>
            
                {/* MPG Input */}
                <div className="mt-2">
                  <label className="block">Or enter efficiency (mpg US):</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-md"
                    value={mpg}
                    onChange={(e) => setMpg(e.target.value)}
                  />
                </div>
            
                {/* Fuel Type Selector */}
                <div className="mt-2">
                  <label className="block">Fuel Type:</label>
                  <select className="w-full p-2 border rounded-md" value={fuelType} onChange={(e) => setFuelType(e.target.value)}>
                    <option value="petrol">Petrol</option>
                    <option value="diesel">Diesel</option>
                    <option value="electric">Electric</option>
                  </select>
                </div>
            
                {/* Calculate Button */}
                <button
                  className="w-full mt-4 p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  onClick={calculateCarFootprint}
                >
                  Calculate
                </button>
            
                {/* Result Display */}
                <p className="mt-4 text-lg font-bold">
                  Total Car Footprint = {totalCarFootprint} metric tons of CO2e
                </p>
            
                <button className="w-full mt-2 p-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                  Offset Now
                </button>
            
                {/* Navigation Buttons */}
                <div className="flex justify-between mt-4">
                  <button className="px-4 py-2 bg-gray-300 rounded-lg">&lt; Flights</button>
                  <button className="px-4 py-2 bg-gray-300 rounded-lg">Food &gt;</button>
                </div>
              </div>
            }

            {activeTab === "Motorbike" && 
              // https://www.carbonfootprint.com/calculator.aspx
              <div className="p-4 bg-gray-700 rounded-lg">
                <h2 className="text-lg font-bold">Calculate the Carbon Footprint for Your Motorbikes</h2>
                <p className="text-sm text-white">You can enter details for up to 2 motorbikes.</p>

                {/* Mileage Input */}
                <div className="mt-4">
                  <label className="block">Mileage (miles):</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-md"
                    value={bikeMileage}
                    onChange={(e) => setBikeMileage(e.target.value)}
                  />
                </div>

                {/* Motorbike Type Selector */}
                <div className="mt-2">
                  <label className="block">Select Motorbike Type:</label>
                  <select className="w-full p-2 border rounded-md" value={bikeType} onChange={(e) => setBikeType(e.target.value)}>
                    <option value="">- Select Type -</option>
                    <option value="scooter">Scooter</option>
                    <option value="cruiser">Cruiser</option>
                    <option value="sports">Sports Bike</option>
                  </select>
                </div>

                {/* MPG Input */}
                <div className="mt-2">
                  <label className="block">Or enter efficiency (mpg US):</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-md"
                    value={bikeMpg}
                    onChange={(e) => setBikeMpg(e.target.value)}
                  />
                </div>

                {/* Calculate Button */}
                <button
                  className="w-full mt-4 p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  onClick={calculateBikeFootprint}
                >
                  Calculate
                </button>

                {/* Result Display */}
                <p className="mt-4 text-lg font-bold">
                  Total Motorbike Footprint = {bikeFootprint} metric tons of CO2e
                </p>

                <button className="w-full mt-2 p-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                  Offset Now
                </button>

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-4">
                  <button className="px-4 py-2 bg-gray-300 rounded-lg">&lt; Car</button>
                  <button className="px-4 py-2 bg-gray-300 rounded-lg">Bus & Rail &gt;</button>
                </div>
              </div>
            }
            {activeTab === "Bus & Rail" && 
              <div className="p-4 bg-gray-700 rounded-lg">
                <h2 className="text-lg font-bold">Calculate the Carbon Footprint for Your Bus & Rail Travel</h2>
                <p className="text-sm text-white">Enter mileage for each type of public transport, and press Calculate.</p>
            
                {/* Input Fields */}
                {[
                  { label: "Bus", state: busMiles, setState: setBusMiles },
                  { label: "Coach", state: coachMiles, setState: setCoachMiles },
                  { label: "Local Train", state: localTrainMiles, setState: setLocalTrainMiles },
                  { label: "Long Distance Train", state: longTrainMiles, setState: setLongTrainMiles },
                  { label: "Tram", state: tramMiles, setState: setTramMiles },
                  { label: "Subway", state: subwayMiles, setState: setSubwayMiles },
                  { label: "Taxi", state: taxiMiles, setState: setTaxiMiles },
                ].map((item, index) => (
                  <div key={index} className="mt-2">
                    <label className="block">{item.label} (miles):</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-md"
                      value={item.state}
                      onChange={(e) => item.setState(Number(e.target.value))}
                    />
                  </div>
                ))}
            
                {/* Calculate Button */}
                <button
                  className="w-full mt-4 p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  onClick={calculatePublicFootprint}
                >
                  Calculate
                </button>
            
                {/* Result Display */}
                <p className="mt-4 text-lg font-bold">
                  Total Bus & Rail Footprint = {publicFootprint} metric tons of CO2e
                </p>
            
                <button className="w-full mt-2 p-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                  Offset Now
                </button>
            
                {/* Navigation Buttons */}
                <div className="flex justify-between mt-4">
                  <button className="px-4 py-2 bg-gray-300 rounded-lg">&lt; Motorbike</button>
                  <button className="px-4 py-2 bg-gray-300 rounded-lg">Secondary &gt;</button>
                </div>
              </div>
            }
            {activeTab === "Plastic" && 
              <div className="p-6 bg-gray-700 shadow-lg rounded-lg">
                <h2 className="text-2xl font-bold text-yellow-200 mb-4">Plastic Calculator</h2>
                <p className="text-white mb-6">
                  Fill out the quiz to find out your plastic footprint.
                </p>
                
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-yellow-200 mb-2">At Home</h3>
                  {Object.keys(homePlastics).map((key) => (
                    <div key={key} className="mb-3">
                      <label className="block text-white capitalize">{key.replace(/([A-Z])/g, ' $1')}</label>
                      <input
                        type="number"
                        className="w-full p-2 border rounded"
                        min="0"
                        value={homePlastics[key]}
                        onChange={(e) =>
                          setHomePlastics({ ...homePlastics, [key]: e.target.value })
                        }
                      />
                    </div>
                  ))}
                  <p className="font-bold mt-2">Home Plastics Total: {calculateTotal(homePlastics)}</p>
                </div>
          
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-yellow-200 mb-2">Out and About</h3>
                  {Object.keys(outPlastics).map((key) => (
                    <div key={key} className="mb-3">
                      <label className="block text-white capitalize">{key.replace(/([A-Z])/g, ' $1')}</label>
                      <input
                        type="number"
                        className="w-full p-2 border rounded"
                        min="0"
                        value={outPlastics[key]}
                        onChange={(e) =>
                          setOutPlastics({ ...outPlastics, [key]: e.target.value })
                        }
                      />
                    </div>
                  ))}
                  <p className="font-bold mt-2">Out and About Plastics Total: {calculateTotal(outPlastics)}</p>
                </div>
          
                <div className="bg-green-100 p-4 rounded-lg text-yellow-200 font-bold">
                  Yearly Plastic Waste: {calculateTotal(homePlastics) + calculateTotal(outPlastics)}
                </div>
              </div>
            }
            {activeTab === "Electrical" && 
              <div className="min-h-screen bg-gray-700 text-gray-900 p-5 md:p-10">
                <div className="bg-gray-700 shadow-lg rounded-lg p-5 md:p-8">
                  <h1 className="text-3xl font-bold text-center mb-6 text-white">
                    Household Energy Consumption
                  </h1>
                  <p className="text-white text-center mb-6">
                    Understand where your energy is used and how to save money and reduce CO₂ emissions.
                  </p>
          
                  <div className="flex justify-end mb-4">
                    <label className="mr-2 font-medium">Sort By:</label>
                    <select
                      onChange={(e) => setSortBy(e.target.value)}
                      className="border p-2 rounded"
                    >
                      <option value="cost">Cost (£/₹)</option>
                      <option value="co2">CO₂ Emissions</option>
                    </select>
                  </div>
          
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-600 text-white">
                          <th className="p-3 text-left">Appliance</th>
                          <th className="p-3 text-left">Usage</th>
                          <th className="p-3 text-right">Cost (£/₹ per year)</th>
                          <th className="p-3 text-right">CO₂ (kg per year)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedData.map((item, index) => (
                          <tr
                            key={index}
                            className={`border-b ${
                              item.appliance.includes("Low Energy Light Bulb")
                                ? "bg-blue-600 text-white font-semibold"
                                : "hover:bg-gray-600"
                            }`}
                          >
                            <td className="p-3">{item.appliance}</td>
                            <td className="p-3">{item.usage}</td>
                            <td className="p-3 text-right">
                              £{item.cost.toFixed(2)} / ₹{(item.cost * conversionRate).toFixed(0)}
                            </td>
                            <td className="p-3 text-right">{item.co2}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            }
            {activeTab === "Secondary" && 
              <div className="p-4 bg-gray-700 rounded-lg">
                <h2 className="text-lg font-bold">Secondary Carbon Footprint Calculator</h2>
                <p className="text-sm text-white">Enter your yearly expenses per category, then press Estimate.</p>
            
                {/* Currency Selector */}
                <div className="mt-2">
                  <label className="block font-bold">Choose your currency:</label>
                  <select className="p-2 border rounded-md w-full" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                    <option value="₹ INR">₹ INR</option>
                    <option value="$ USD">$ USD</option>
                  </select>
                </div>
            
                {/* Expense Input Fields */}
                {[
                  { label: "Food & Drink Products", key: "food" },
                  { label: "Pharmaceuticals", key: "pharmaceuticals" },
                  { label: "Clothes, Textiles & Shoes", key: "clothes" },
                  { label: "Paper Products (Books, Magazines)", key: "paperProducts" },
                  { label: "Computers & IT Equipment", key: "computers" },
                  { label: "TV, Radio & Phone Equipment", key: "electronics" },
                  { label: "Motor Vehicles (Excluding Fuel)", key: "vehicles" },
                  { label: "Furniture & Manufactured Goods", key: "furniture" },
                  { label: "Hotels, Restaurants & Pubs", key: "hospitality" },
                  { label: "Telephone & Mobile Costs", key: "telecom" },
                  { label: "Banking & Finance", key: "finance" },
                  { label: "Insurance", key: "insurance" },
                  { label: "Education", key: "education" },
                  { label: "Recreational & Cultural Activities", key: "recreation" },
                ].map((item, index) => (
                  <div key={index} className="mt-2">
                    <label className="block">{item.label} (₹ per year):</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-md"
                      value={expenses[item.key]}
                      onChange={(e) =>
                        setExpenses({ ...expenses, [item.key]: Number(e.target.value) })
                      }
                    />
                  </div>
                ))}
            
                {/* Estimate Button */}
                <button
                  className="w-full mt-4 p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  onClick={calculateSecondaryFootprint}
                >
                  Estimate
                </button>
            
                {/* Result Display */}
                <p className="mt-4 text-lg font-bold">
                  Total Secondary Footprint = {secondaryFootprint} metric tons of CO2e
                </p>
            
                <button className="w-full mt-2 p-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                  Offset Now
                </button>
            
                {/* Navigation Buttons */}
                <div className="flex justify-between mt-4">
                  <button className="px-4 py-2 bg-gray-300 rounded-lg">&lt; Bus & Rail</button>
                  <button className="px-4 py-2 bg-gray-300 rounded-lg">Waste &gt;</button>
                </div>
              </div>
            }
            {activeTab === "Results" && 
               <div className="p-4 bg-gray-700 rounded-lg">
                <h2 className="text-lg font-bold">Your Carbon Footprint</h2>
          
                <div className="mt-2 p-2 bg-white rounded-md shadow-md">
                  {Object.keys(selectedOffsets).map((category) => (
                    <div key={category} className="flex justify-between items-center border-b p-2">
                      <span className="capitalize">{category.replace(/_/g, " ")}</span>
                      <span className="font-bold">{selectedOffsets[category].toFixed(2)} metric tons</span>
                      <input
                        type="checkbox"
                        checked={selectedOffsets.includes(category)}
                        onChange={() => toggleOffset(category)}
                      />
                    </div>
                  ))}
          
                  <div className="flex justify-between p-2 font-bold">
                    <span>Total =</span>
                    <span>{totalFootprint.toFixed(2)} metric tons of CO2e</span>
                  </div>
                </div>
          
                <p className="mt-4 text-sm text-white">
                  Click the sections you want to offset, then press "Offset Now."
                </p>
          
                <div className="flex justify-between mt-4 p-2 bg-white rounded-md shadow-md">
                  <span>Total To Offset:</span>
                  <span className="font-bold">{totalOffset.toFixed(2)} metric tons of CO2e</span>
                </div>
          
                <button
                  className="w-full mt-2 p-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                  disabled={totalOffset === 0}
                >
                  Offset Now
                </button>
          
                {/* Footprint Comparison Section */}
                <div className="mt-4 p-4 bg-gray-600 rounded-md shadow-md">
                  <h3 className="text-md font-bold">Your Footprint vs. Averages</h3>
                  <p>Your footprint: <strong>{totalFootprint.toFixed(2)} metric tons per year</strong></p>
                  <p>US average: <strong>15.24 metric tons</strong></p>
                  <p>EU average: <strong>6.8 metric tons</strong></p>
                  <p>Worldwide average: <strong>4.79 metric tons</strong></p>
                  <p>Climate goal: <strong>0 metric tons</strong></p>
                </div>
              </div>
            }
          </div>
        </div>
      </MainLayout>
    </>
  );
};

export default CoolEarthCalculator;
